import { useEffect, useRef, useState } from "react";
import { useTheme } from "../../theme/ThemeProvider";

/*
  Порядок загрузки картинок шапки по теме.

  Раньше оба слоя монтировались сразу, и в тёмной теме первый кадр с
  картинкой был светлым — тёмная проявлялась поверх него. Теперь при первом
  рендере в DOM только слой активной темы, второй доезжает в простое
  (requestIdleCallback с таймаутом 3 с) и ждёт переключателя.

  Переходы включаются не раньше второго кадра: класс is-ready вешается в
  requestAnimationFrame, до него transition нет, иначе первый же слой
  проявлялся бы анимацией.

  Если тему переключили раньше простоя, второй слой монтируем немедленно:
  кроссфейда не будет (элемент только что вставлен), но пустого кадра тоже.
*/
const IDLE_TIMEOUT_MS = 3000;

export const useThemeImageLayers = (lightSrc, darkSrc) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const hasLight = Boolean(lightSrc);
  const hasDark = Boolean(darkSrc);

  const [ready, setReady] = useState(false);
  const [bothMounted, setBothMounted] = useState(false);
  const initialTheme = useRef(theme);

  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!hasLight || !hasDark || bothMounted) return undefined;
    if (theme !== initialTheme.current) {
      setBothMounted(true);
      return undefined;
    }
    const run = () => setBothMounted(true);
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(run, IDLE_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [hasLight, hasDark, bothMounted, theme]);

  // какой слой активен в текущей теме: он грузится eager, второй — lazy
  const primary = hasDark && isDark ? "dark" : "light";

  return {
    ready,
    primary,
    // порядок в DOM постоянный: светлый слой первым, тёмный вторым (он
    // перекрывает светлый без z-index). Меняется только наличие.
    showLight: hasLight && (bothMounted || !hasDark || !isDark),
    showDark: hasDark && (bothMounted || isDark),
  };
};

export default useThemeImageLayers;
